import { UseSearchResult } from "@saleor/hooks/makeSearch";
import { maybe } from "@saleor/misc";
import {
  SearchBrands,
  SearchBrandsVariables
} from "@saleor/searches/types/SearchBrands";
import {
  SearchMalls,
  SearchMallsVariables
} from "@saleor/searches/types/SearchMalls";

import { IFilterElement } from "../../../../components/Filter";
import {
  createFilterTabUtils,
  createFilterUtils,
  dedupeFilter,
  getMultipleValueQueryParam
} from "../../../../utils/filters";
import { removeBase64Name } from "../../../utils/misc";
import {
  StoreDetailsFinanceFilterKeys,
  StoreDetailsFinanceFilterOpts
} from "../../components/StoreDetailsFinancePage/filters";
import {
  InitialStoreDetailsFinanceFilterData_brands_edges_node,
  InitialStoreDetailsFinanceFilterData_malls_edges_node
} from "../../types/InitialStoreDetailsFinanceFilterData";
import {
  StoreDetailsFinanceListUrlFilters,
  StoreDetailsFinanceListUrlFiltersEnum,
  StoreDetailsFinanceListUrlFiltersWithMultipleValues,
  StoreDetailsFinanceListUrlQueryParams
} from "../../url";
export const STORE_DETAILS_FILTERS_KEY = "StoreDetailsFinanceFilter";

export function getFilterOpts(
  params: StoreDetailsFinanceListUrlFilters,
  brands: {
    initial: InitialStoreDetailsFinanceFilterData_brands_edges_node[];
    search: UseSearchResult<SearchBrands, SearchBrandsVariables>;
  },
  malls: {
    initial: InitialStoreDetailsFinanceFilterData_malls_edges_node[];
    search: UseSearchResult<SearchMalls, SearchMallsVariables>;
  }
): StoreDetailsFinanceFilterOpts {
  return {
    brand: {
      active: !!params.brand,
      choices: maybe(
        () =>
          brands.search.result.data.search.edges.map(edge => ({
            label: edge.node.name,
            value: edge.node.id
          })),
        []
      ),
      displayValues: !!params.brand
        ? maybe(
            () =>
              brands.initial.map(brand => ({
                label: brand.name,
                value: brand.id
              })),
            []
          )
        : [],
      hasMore: maybe(
        () => brands.search.result.data.search.pageInfo.hasNextPage,
        false
      ),
      initialSearch: "",
      loading: brands.search.result.loading,
      onFetchMore: brands.search.loadMore,
      onSearchChange: brands.search.search,
      value: maybe(() => dedupeFilter(params.brand), [])
    },
    mall: {
      active: !!params.mall,
      choices: maybe(
        () =>
          malls.search.result.data.search.edges.map(edge => ({
            label: edge.node.name,
            value: edge.node.id
          })),
        []
      ),
      displayValues: !!params.mall
        ? maybe(
            () =>
              malls.initial.map(mall => ({
                label: mall.name,
                value: mall.id
              })),
            []
          )
        : [],
      hasMore: maybe(
        () => malls.search.result.data.search.pageInfo.hasNextPage,
        false
      ),
      initialSearch: "",
      loading: malls.search.result.loading,
      onFetchMore: malls.search.loadMore,
      onSearchChange: malls.search.search,
      value: maybe(() => dedupeFilter(params.mall), [])
    },
    storeStatus: {
      active: !!params.storeStatus,
      value: maybe(() => dedupeFilter(params.storeStatus), [])
    }
  };
}

export function getFilterVariables(params: StoreDetailsFinanceListUrlFilters) {
  return {
    brandId: removeBase64Name(
      "Brand:",
      params.brand !== undefined ? params.brand : []
    ),
    mallId: removeBase64Name(
      "Mall:",
      params.mall !== undefined ? params.mall : []
    ),
    search: params.query,
    storeStatus: maybe(() => params.storeStatus, null)
  };
}

export function getFilterQueryParam(
  filter: IFilterElement<StoreDetailsFinanceFilterKeys>
): StoreDetailsFinanceListUrlFilters {
  const { name } = filter;

  switch (name) {
    case StoreDetailsFinanceFilterKeys.mall:
      return getMultipleValueQueryParam(
        filter,
        StoreDetailsFinanceListUrlFiltersWithMultipleValues.mall
      );
    case StoreDetailsFinanceFilterKeys.brand:
      return getMultipleValueQueryParam(
        filter,
        StoreDetailsFinanceListUrlFiltersWithMultipleValues.brand
      );
    case StoreDetailsFinanceFilterKeys.storeStatus:
      return getMultipleValueQueryParam(
        filter,
        StoreDetailsFinanceListUrlFiltersWithMultipleValues.storeStatus
      );
  }
}

export const {
  deleteFilterTab,
  getFilterTabs,
  saveFilterTab
} = createFilterTabUtils<StoreDetailsFinanceListUrlFilters>(
  STORE_DETAILS_FILTERS_KEY
);

export const { areFiltersApplied, getActiveFilters } = createFilterUtils<
  StoreDetailsFinanceListUrlQueryParams,
  StoreDetailsFinanceListUrlFilters
>({
  ...StoreDetailsFinanceListUrlFiltersEnum,
  ...StoreDetailsFinanceListUrlFiltersWithMultipleValues
});
